import { useState } from "react";
import { App as AntdApp, Alert, Button, Modal, Space, Table, Tag, Typography, message } from "antd";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { CopyOutlined, PlusOutlined } from "@ant-design/icons";
import type {
  ApiKeySummary,
  IssueKeyResponse,
  KeysListResponse,
  RevokeKeyResponse,
  RotateKeyResponse,
} from "@coco-mailrelay/shared-types";
import { apiRequest } from "../../lib/apiRequest";
import { errorMessage } from "../../lib/errors";
import { GaCancelledError } from "../../lib/ga-interceptor";

const { Text, Paragraph } = Typography;

/** Issue / rotate / revoke API keys (docs/305 §3.4). All three are step-up ops. */
export default function KeysTab({ tenantId }: { tenantId: string }): JSX.Element {
  const queryClient = useQueryClient();
  const { modal } = AntdApp.useApp();
  const [plaintext, setPlaintext] = useState<string | null>(null);

  const keysQuery = useQuery({
    queryKey: ["keys", tenantId],
    queryFn: () => apiRequest<KeysListResponse>(`/tenants/${tenantId}/keys`),
  });

  const invalidate = (): void => {
    void queryClient.invalidateQueries({ queryKey: ["keys", tenantId] });
    void queryClient.invalidateQueries({ queryKey: ["tenant", tenantId] });
  };

  const issueMutation = useMutation({
    mutationFn: () => apiRequest<IssueKeyResponse>(`/tenants/${tenantId}/keys`, { method: "POST" }),
    onSuccess: (res) => {
      setPlaintext(res.key.plaintext);
      invalidate();
    },
    onError: (err) => {
      if (err instanceof GaCancelledError) return;
      message.error(errorMessage(err, "Failed to issue key"));
    },
  });

  const rotateMutation = useMutation({
    mutationFn: (keyId: string) => apiRequest<RotateKeyResponse>(`/keys/${keyId}/rotate`, { method: "POST" }),
    onSuccess: (res) => {
      setPlaintext(res.key.plaintext);
      invalidate();
    },
    onError: (err) => {
      if (err instanceof GaCancelledError) return;
      message.error(errorMessage(err, "Failed to rotate key"));
    },
  });

  const revokeMutation = useMutation({
    mutationFn: (keyId: string) => apiRequest<RevokeKeyResponse>(`/keys/${keyId}/revoke`, { method: "POST" }),
    onSuccess: () => {
      message.success("Key revoked");
      invalidate();
    },
    onError: (err) => {
      if (err instanceof GaCancelledError) return;
      message.error(errorMessage(err, "Failed to revoke key"));
    },
  });

  // `onOk` returns void (not a promise) so Antd closes the confirm dialog
  // immediately on click. Awaiting the request inside `onOk` instead would keep
  // the confirm modal open with a button spinner, and the step-up TOTP modal
  // (popped by the 428 interceptor) renders *behind* it — covering the code
  // input. Firing the mutation and letting the confirm close means only one
  // modal is ever on screen: confirm → step-up.
  const rotate = (keyId: string): void => {
    modal.confirm({
      title: "Rotate key?",
      content: "Issues a new key and soft-expires this one after the grace window. Requires step-up.",
      okText: "Rotate",
      onOk: () => {
        rotateMutation.mutate(keyId);
      },
    });
  };

  const revoke = (keyId: string): void => {
    modal.confirm({
      title: "Revoke key?",
      content: "Immediately revokes this key. Requires step-up.",
      okText: "Revoke",
      okButtonProps: { danger: true },
      onOk: () => {
        revokeMutation.mutate(keyId);
      },
    });
  };

  const copyPlaintext = async (): Promise<void> => {
    if (!plaintext) return;
    try {
      await navigator.clipboard.writeText(plaintext);
      message.success("Copied to clipboard");
    } catch {
      message.warning("Copy failed — select and copy manually");
    }
  };

  return (
    <Space direction="vertical" size="middle" style={{ width: "100%" }}>
      <Button type="primary" icon={<PlusOutlined />} loading={issueMutation.isPending} onClick={() => issueMutation.mutate()}>
        Issue key
      </Button>

      <Table<ApiKeySummary>
        rowKey="id"
        loading={keysQuery.isLoading}
        dataSource={keysQuery.data?.keys ?? []}
        pagination={false}
        columns={[
          {
            title: "Key",
            key: "masked",
            render: (_, k) => (
              <Text code>
                {k.key_prefix}…{k.last4}
              </Text>
            ),
          },
          {
            title: "Status",
            dataIndex: "status",
            key: "status",
            render: (s: ApiKeySummary["status"]) => <Tag color={s === "active" ? "green" : "red"}>{s}</Tag>,
          },
          { title: "Created", dataIndex: "created_at", key: "created_at", render: fmt },
          { title: "Expires", dataIndex: "expires_at", key: "expires_at", render: fmt },
          {
            title: "Actions",
            key: "actions",
            render: (_, k) =>
              k.status === "active" ? (
                <Space>
                  <Button
                    size="small"
                    loading={rotateMutation.isPending && rotateMutation.variables === k.id}
                    onClick={() => rotate(k.id)}
                  >
                    Rotate
                  </Button>
                  <Button
                    size="small"
                    danger
                    loading={revokeMutation.isPending && revokeMutation.variables === k.id}
                    onClick={() => revoke(k.id)}
                  >
                    Revoke
                  </Button>
                </Space>
              ) : null,
          },
        ]}
      />

      <Modal
        title="API key — copy it now"
        open={plaintext !== null}
        onCancel={() => setPlaintext(null)}
        footer={[
          <Button key="copy" type="primary" icon={<CopyOutlined />} onClick={copyPlaintext}>
            Copy
          </Button>,
          <Button key="done" onClick={() => setPlaintext(null)}>
            Done
          </Button>,
        ]}
      >
        <Alert
          type="warning"
          showIcon
          message="This is the only time the full key is shown."
          description="Copy and store it securely now — it cannot be retrieved again."
          style={{ marginBottom: 12 }}
        />
        <Paragraph
          copyable={{ text: plaintext ?? "" }}
          code
          style={{ wordBreak: "break-all", background: "#fafafa", padding: 12, borderRadius: 6 }}
        >
          {plaintext}
        </Paragraph>
      </Modal>
    </Space>
  );
}

function fmt(value: string | null): string {
  return value ? new Date(value).toLocaleString() : "—";
}
